import React from "react";
import PropTypes from "prop-types";
import { CheckCircleFilled } from "@ant-design/icons";
import {
    DEFAULT_OPTION_SETS,
    formatDate,
    getTypeLabel,
    isChoiceType,
} from "./questionHelpers";

const Row = ({ label, children }) => (
    <div className="qdetail__row">
        <span className="qdetail__label">{label}</span>
        <div className="qdetail__value">{children}</div>
    </div>
);

const OptionList = ({ question }) => {
    if (question.isDefaultOptions) {
        const set = DEFAULT_OPTION_SETS[(question.defaultOptionSet || 1) - 1];
        return (
            <>
                <div className="qdetail__hint">
                    Default option set {question.defaultOptionSet || 1}
                </div>
                <ol className="qdetail__options">
                    {(set?.options || []).map((text, i) => (
                        <li key={i}>{text}</li>
                    ))}
                </ol>
            </>
        );
    }

    const options = question.options || [];
    if (!options.length) return <span className="qdetail__empty">No options</span>;

    return (
        <ol className="qdetail__options">
            {options.map((opt, i) => (
                <li key={opt.optionId || i} className={opt.isCorrect ? "is-correct" : ""}>
                    {opt.optionText || <span className="qdetail__empty">(empty)</span>}
                    {opt.isCorrect && (
                        <CheckCircleFilled className="qdetail__correct" title="Correct answer" />
                    )}
                </li>
            ))}
        </ol>
    );
};

/** Read-only view of a complete question: text, image, answer key, options and sub-questions. */
const QuestionDetailView = ({ question, syllabusNameById = {}, compact = false }) => {
    if (!question) return null;

    const syllabusName =
        syllabusNameById[question.syllabusId ?? question.SyllabusId] || null;

    return (
        <div className={`qdetail ${compact ? "qdetail--compact" : ""}`}>
            <Row label="Question">
                <div className="qdetail__question">{question.questionText || "—"}</div>
            </Row>

            <div className="qdetail__meta">
                <span>
                    <b>Type:</b> {getTypeLabel(question.questionType)}
                </span>
                <span>
                    <b>Order:</b> {question.sortOrder ?? "—"}
                </span>
                {question.moduleName && (
                    <span>
                        <b>Module:</b> {question.moduleName}
                    </span>
                )}
                {syllabusName && (
                    <span>
                        <b>Syllabus:</b> {syllabusName}
                    </span>
                )}
            </div>

            {question.questionMediaUrl && (
                <Row label="Image">
                    <img
                        className="qdetail__image"
                        src={question.questionMediaUrl}
                        alt="Question"
                    />
                </Row>
            )}

            {isChoiceType(question.questionType) && (
                <Row label="Options">
                    <OptionList question={question} />
                </Row>
            )}

            {(question.questionType === 1 || question.questionType === 2) && (
                <Row label="Answer key">
                    {question.answerKeyText || <span className="qdetail__empty">Not set</span>}
                </Row>
            )}

            {question.questionType === 5 && (
                <Row label="Answer key date">
                    {question.answerKeyDate ? (
                        formatDate(question.answerKeyDate)
                    ) : (
                        <span className="qdetail__empty">Not set</span>
                    )}
                </Row>
            )}

            {question.questionType === 6 && (
                <Row label={`Sub-questions (${(question.childQuestions || []).length})`}>
                    {(question.childQuestions || []).length === 0 ? (
                        <span className="qdetail__empty">None</span>
                    ) : (
                        <div className="qdetail__children">
                            {(question.childQuestions || []).map((child, i) => (
                                <div className="qdetail__child" key={child.questionId || i}>
                                    <div className="qdetail__childHead">
                                        {i + 1}. {child.questionText || "—"}
                                        <span className="qdetail__childType">
                                            {getTypeLabel(child.questionType)}
                                        </span>
                                    </div>
                                    {isChoiceType(child.questionType) && (
                                        <OptionList question={child} />
                                    )}
                                    {(child.questionType === 1 || child.questionType === 2) &&
                                        child.answerKeyText && (
                                            <div className="qdetail__hint">
                                                Answer key: {child.answerKeyText}
                                            </div>
                                        )}
                                    {child.questionType === 5 && child.answerKeyDate && (
                                        <div className="qdetail__hint">
                                            Answer key: {formatDate(child.answerKeyDate)}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </Row>
            )}
        </div>
    );
};

QuestionDetailView.propTypes = {
    question: PropTypes.object,
    syllabusNameById: PropTypes.object,
    compact: PropTypes.bool,
};

export default QuestionDetailView;
